import { DateInput } from "components/DateInput";
import { Form, Formik } from "formik";
import TimePickerField from "components/TimePicker/TimePicker";
import { Button } from "newui/components/Button";

import * as Yup from "yup";
import { useEffect, useRef, useState } from "react";
import { updateSubmissionDeadLine } from "actions/RFQ/rfqsAction";
import moment from "moment";
import {
	Drawer,
	DrawerBody,
	DrawerContent,
	DrawerHeader,
	DrawerOverlay,
	Flex,
	Spinner,
} from "@chakra-ui/react";
import { showToast } from "utils/helpers";
import { CloseButton } from "components/RFQ/CloseButton";
import { isNull } from "utils/validation";
import { FormattedInput } from "components/FormInputs/FormattedInput";
import { SubmissionDeadlineExtensionEmailContent } from "components/email-template/SubmissionDeadlineExtensionEmailContent";
import EmailPreview from "pages/EmailPreview/EmailPreview";
import { useRfxMail } from "components/email-template/hooks/useRfxMail";
import { isRfxOnboardingType } from "newui/features/Rfx/utils";

const ExtendSubmissionDeadlineModal = ({
	isModalOpen,
	rfqDetail,
	onSubmissionDateExtension,
	onClose,
	props,
}) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const formikRef = useRef();
	const [formValues, setFormValues] = useState();
	const [newDeadline, setNewDeadline] = useState();

	const { rfxMail, additionalMessage, handleAdditionalMessageChange } =
		useRfxMail(rfqDetail);

	const [newDate, setNewDateValue] = useState(
		rfxMail?.emailData?.deadline_date
	);
	const [newTime, setNewTimeValue] = useState(
		rfxMail?.emailData?.deadline_time
	);
	const [newTimeZone, setNewTimeZoneValue] = useState(
		rfxMail?.emailData?.deadline_time_zone
	);

	const handleOnClose = () => {
		setNewDeadline(null);
		if (onClose) {
			onClose();
		}
	};

	const onSubmit = (formObject) => {
		const newDeadline = formValues?.extended_time
			? moment(
					moment(formValues?.extended_date).format("YYYY-MM-DD") +
						" " +
						formValues?.extended_time +
						":00"
				)
			: "";

		const data = {
			submission_deadline: newDeadline.format("YYYY-MM-DD HH:mm:ss"),
			submission_deadline_timezone: formValues.submission_deadline_timezone,
			additional_message: formValues?.additional_message,
		};

		setIsSubmitting(true);
		updateSubmissionDeadLine(rfqDetail.id, data)
			.then((res) => {
				onSubmissionDateExtension(res.data.data);
				handleOnClose();
				showToast("Submission Deadline Extended Successfully", "Success", true);
			})
			.catch((e) => {
				showToast(e.response?.data?.message, "Error");
			})
			.finally(() => {
				setIsSubmitting(false);
			});
	};

	const handleDateChange = (dateValue) => {
		setNewDateValue(dateValue);
	};

	const handleTimeChange = (timeString) => {
		setNewTimeValue(timeString);
	};

	const handleTimeZoneChange = (timeString) => {
		setNewTimeZoneValue(timeString);
	};

	useEffect(() => {
		const date = newDate ?? rfxMail?.emailData?.deadline_date;
		const time = newTime ?? rfxMail?.emailData?.deadline_24_time;
		const timeZone = newTimeZone ?? rfxMail?.emailData?.deadline_time_zone;

		const submissionDeadline =
			newTime || newDate || newTimeZone
				? moment(moment(date).format("YYYY-MM-DD") + " " + time + ":00").format(
						"dddd D MMMM YYYY h:mm A "
					) + timeZone
				: "";

		setNewDeadline(submissionDeadline);
	}, [
		newDate,
		newTime,
		newTimeZone,
		rfxMail?.emailData?.deadline_date,
		rfxMail?.emailData?.deadline_time,
		rfxMail?.emailData?.deadline_time_zone,
		rfxMail?.emailData?.deadline_24_time,
	]);

	const isOnboardingRfx = isRfxOnboardingType(
		rfqDetail?.rfxType ?? rfqDetail?.type
	);

	return (
		<>
			<Drawer
				placement={"right"}
				isOpen={isModalOpen}
				onClose={handleOnClose}
				size="xl"
				{...props}
				className="bg-white p-11"
			>
				<DrawerOverlay onClick={handleOnClose} />
				<DrawerContent maxWidth={"1280px"}>
					<DrawerHeader className="border-b-2">
						<Flex
							justifyContent="space-between"
							align={"center"}
						>
							<div>
								<p className="text-xl font-semibold text-bg-brand-solid">
									Submission Deadline Extension
								</p>
							</div>
							<CloseButton onClose={handleOnClose} />
						</Flex>
					</DrawerHeader>
					<DrawerBody>
						<div className="grid grid-cols-3">
							<div className="relative">
								<div className="fixed">
									<div>
										<Formik
											innerRef={formikRef}
											validationSchema={Yup.object().shape({
												extended_date: Yup.string().required(
													"This field is required"
												),
												extended_time: Yup.string().required(
													"This field is required"
												),
												additional_message: Yup.string().required(
													"This field is required"
												),
											})}
											initialValues={{
												extended_date: rfqDetail?.submissionDeadline,
												extended_time: moment(
													rfqDetail?.submissionDeadline
												).format("HH:mm"),
												submission_deadline_timezone:
													rfqDetail?.submissionTimezone,
												additional_message: "",
											}}
											onSubmit={onSubmit}
										>
											{(props) => {
												const { setFieldValue, values } = props;
												setFormValues(values);
												return (
													<Form>
														<DateInput
															name="extended_date"
															label="Extended Deadline"
															setFieldValue={setFieldValue}
															values={values}
															minDate={moment(
																moment(rfqDetail?.submissionDeadline).format(
																	"YYYY/MM/D"
																)
															).toDate()}
															minDateTooltipContent="current Submission Deadline"
															onChange={handleDateChange}
														/>
														<TimePickerField
															className="mt-9 w-full flex-1"
															name="extended_time"
															nameTimezone="submission_deadline_timezone"
															label="Extended Time"
															onTimeChange={handleTimeChange}
															onTimeZoneChange={handleTimeZoneChange}
														/>
														<FormattedInput
															value={additionalMessage}
															label="Reason for Extension"
															name="additional_message"
															characterLimit={500}
															placeholder="Enter reason for extension to put in email"
															required
															error={
																isNull(values.additional_message) &&
																"Reason for Extension is required"
															}
															onChange={(e) => {
																handleAdditionalMessageChange(e);
																return setFieldValue("additional_message", e);
															}}
														/>
														<Button
															type="submit"
															btntype="submit"
															className="my-4 !w-full !py-3"
														>
															Extend Time
														</Button>
														{isSubmitting && (
															<div className="flex items-center justify-center gap-4 pt-4 text-royal-blue">
																<Spinner />
																<p className="text-center font-bold">
																	Submitting ....
																</p>
															</div>
														)}
													</Form>
												);
											}}
										</Formik>
									</div>
								</div>
							</div>
							<div className="col-span-2">
								<EmailPreview>
									<SubmissionDeadlineExtensionEmailContent
										rfxMail={rfxMail}
										newDeadline={newDeadline}
										isOnboarding={isOnboardingRfx}
									/>
								</EmailPreview>
							</div>
						</div>
					</DrawerBody>
				</DrawerContent>
			</Drawer>
		</>
	);
};

export default ExtendSubmissionDeadlineModal;
